import { format, parseISO } from 'date-fns'
import { TrackerTradeStatus, type TrackerRevisionStrategy } from '../../api/types/portfolio-tracker.types'

export const TRADES_PER_PAGE = 10

/** The builder holds up to this many strategies in one portfolio. */
export const BUILDER_MOST_STRATEGIES = 6

/** The symbol a builder portfolio holds one strategy of. A strategy without one blocks nothing. */
export const symbolOf = (strategy: TrackerRevisionStrategy): string => strategy.chartedSymbol ?? `key:${strategy.key}`

const MINUS = '−'
const DAY_MS = 864e5

export interface DatedValue {
  date: string
  value: number
}

export const STATUS_COLOURS: Record<TrackerTradeStatus, string> = {
  [TrackerTradeStatus.LinesUp]: 'success',
  [TrackerTradeStatus.PriceDiffers]: 'warning',
  [TrackerTradeStatus.DatesDiffer]: 'warning',
  [TrackerTradeStatus.NoSignal]: 'danger',
  [TrackerTradeStatus.NoBuilderTrade]: 'danger',
  [TrackerTradeStatus.NoPriceYet]: 'secondary',
}

export const formatMoney = (value: number): string => {
  const rounded = Math.round(value)
  return `${rounded < 0 ? MINUS : ''}$${Math.abs(rounded).toLocaleString('en-US')}`
}

export const formatSignedMoney = (value: number): string => {
  const rounded = Math.round(value)
  const sign = rounded > 0 ? '+' : rounded < 0 ? MINUS : ''
  return `${sign}$${Math.abs(rounded).toLocaleString('en-US')}`
}

/** "20%", "33.3%". */
export const formatPercent = (value: number): string =>
  `${(Math.round(value * 10) / 10).toLocaleString('en-US')}%`

/** "+12.4%", "−3.0%". */
export const formatSignedPercent = (value: number): string => {
  const rounded = Math.round(value * 10) / 10
  const sign = rounded > 0 ? '+' : rounded < 0 ? MINUS : ''
  return `${sign}${Math.abs(rounded).toFixed(1)}%`
}

/** "+1.4", "−0.6": a difference in percentage points, without the unit. */
export const formatSignedPoints = (value: number): string =>
  `${value > 0 ? '+' : value < 0 ? MINUS : ''}${Math.abs(value).toFixed(1)}`

/** "+3", "−12". */
export const formatSignedCount = (value: number): string =>
  `${value > 0 ? '+' : value < 0 ? MINUS : ''}${Math.abs(value).toLocaleString('en-US')}`

/** A fall from a high, given as a positive percent: "−4.3%". */
export const formatFall = (percent: number): string => (percent >= 0.05 ? `${MINUS}${formatPercent(percent)}` : '0%')

/** Cents dropped from $1,000 up, so a crypto price fits beside its date. */
export const formatPrice = (value: number): string => {
  const digits = value >= 1000 ? 0 : value < 10 ? 4 : 2
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits })}`
}

/** "21 Jan 26", short enough for a trade's two dates side by side. */
export const formatTradeDay = (day: string): string => format(parseISO(day), 'd MMM yy')

/** The value a stepped series holds on `day`. */
export function valueAt(points: DatedValue[], day: string): number {
  let value = points[0]?.value ?? 0
  for (const point of points) {
    if (point.date > day) break
    value = point.value
  }
  return value
}

export const CHART_PAD = { left: 58, right: 14, top: 12, bottom: 26 }

export function niceStep(raw: number): number {
  const power = 10 ** Math.floor(Math.log10(raw))
  const ratio = raw / power
  return (ratio <= 1 ? 1 : ratio <= 2 ? 2 : ratio <= 2.5 ? 2.5 : ratio <= 5 ? 5 : 10) * power
}

/** "$120k", "−$2.5k", "$1.25m". */
export function axisMoney(value: number): string {
  const size = Math.abs(value)
  const sign = value < 0 ? MINUS : ''
  if (size >= 1e6) return `${sign}$${+(size / 1e6).toFixed(2)}m`
  if (size >= 1e3) return `${sign}$${+(size / 1e3).toFixed(1)}k`
  return `${sign}$${Math.round(size)}`
}

export interface ChartFrame {
  width: number
  height: number
  left: number
  right: number
  y: (value: number) => number
  x: (day: string) => number
  /** The day under a point across the plot, kept within the chart's days. */
  dayAt: (px: number) => string
  gridlines: { value: number; y: number }[]
}

/** Round-number gridlines over `low` to `high`, and days across from `firstDay` to `lastDay`. */
export function chartFrame(
  width: number,
  height: number,
  low: number,
  high: number,
  firstDay: string,
  lastDay: string,
): ChartFrame {
  const step = niceStep((high - low) / 4 || 1)
  const lo = Math.floor(low / step) * step
  let hi = Math.ceil(high / step) * step
  if (hi === lo) hi = lo + step

  const y = (value: number) => CHART_PAD.top + ((hi - value) / (hi - lo)) * (height - CHART_PAD.top - CHART_PAD.bottom)
  const left = CHART_PAD.left
  const right = width - CHART_PAD.right
  const t0 = Date.parse(firstDay)
  const span = Math.max(DAY_MS, Date.parse(lastDay) - t0)
  const x = (day: string) => left + ((Date.parse(day) - t0) / span) * (right - left)
  const dayAt = (px: number) => {
    const clamped = Math.min(right, Math.max(left, px))
    const day = new Date(t0 + ((clamped - left) / (right - left)) * span).toISOString().slice(0, 10)
    return day < firstDay ? firstDay : day > lastDay ? lastDay : day
  }

  const gridlines: ChartFrame['gridlines'] = []
  for (let value = lo; value <= hi + step / 2; value += step) gridlines.push({ value, y: y(value) })

  return { width, height, left, right, y, x, dayAt, gridlines }
}

/** Room above and below the values, so a flat line still has an axis to sit on. */
export function lineBounds(points: DatedValue[], base: number): [number, number] {
  const values = points.map((point) => point.value).concat(base)
  const low = Math.min(...values)
  const high = Math.max(...values)
  const room = Math.max((high - low) * 0.08, Math.abs(base) * 0.01, 1)
  return [low - room, high + room]
}

const MONTH: Intl.DateTimeFormatOptions = { month: 'short', timeZone: 'UTC' }
const MONTH_YEAR: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric', timeZone: 'UTC' }
const DAY_MONTH: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' }

/** Labels along the bottom: a week apart over a short stretch, otherwise months, at most `most` of them. */
export function timeTicks(from: string, to: string, most = 8): { day: string; label: string }[] {
  const ticks: { day: string; label: string }[] = []
  if ((Date.parse(to) - Date.parse(from)) / DAY_MS < 50) {
    for (let at = Date.parse(from) + 7 * DAY_MS; at < Date.parse(to); at += 7 * DAY_MS) {
      ticks.push({ day: new Date(at).toISOString().slice(0, 10), label: new Date(at).toLocaleDateString('en-US', DAY_MONTH) })
    }
    const everyWeeks = Math.ceil(ticks.length / most)
    return ticks.filter((_, index) => index % everyWeeks === 0)
  }

  let [year, month] = from.split('-').map(Number)
  for (;;) {
    month++
    if (month > 12) {
      month = 1
      year++
    }
    const day = `${year}-${String(month).padStart(2, '0')}-01`
    if (day > to) break
    const date = new Date(Date.parse(day))
    ticks.push({ day, label: date.toLocaleDateString('en-US', month === 1 ? MONTH_YEAR : MONTH) })
  }
  const every = Math.ceil(ticks.length / most)
  return ticks.filter((_, index) => index % every === 0)
}

/** A stepped line through the points: flat until each point, then straight up or down to it. */
export function stepPath(points: { x: number; y: number }[]): string {
  if (!points.length) return ''
  let path = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`
  for (let index = 1; index < points.length; index++) {
    path += `H${points[index].x.toFixed(1)}V${points[index].y.toFixed(1)}`
  }
  return path
}
